import { useMutation, useQuery } from '@apollo/client';
import CancelIcon from '@mui/icons-material/Cancel';
import { Box, Button, Grid, InputBase, useTheme } from '@mui/material';

import { useFormik } from 'formik';
import type React from 'react';
import { type ChangeEvent, type Dispatch, type SetStateAction, useState } from 'react';
import { type DragSourceMonitor, type DropTargetMonitor, useDrag, useDrop } from 'react-dnd';
import { toast } from 'react-toastify';
import { Title } from 'src/Administration/Common';
import allFieldDefinitionsQuery from 'src/api/admin/allFieldDefinitionsQuery';
import createViewFieldMutation from 'src/api/admin/createViewFieldMutation';
import deleteViewFieldMutation from 'src/api/admin/deleteViewFieldMutation';
import updateViewFieldMutation from 'src/api/admin/updateViewFieldMutation';
import allViews from 'src/api/viewModels/allViews';
import SHOW_METADATA_TAB_IMAGE from 'src/assets/viewsImages/showMetadataTabImage.png';
import ImageHover from 'src/components/ImageHover/ImageHover';
import * as Yup from 'yup';
import styles from './ShowMetadataTabStyles';

const GRID_CONTAINER_SPACING = 4;

// Type definitions for the GraphQL data structures
interface FieldDef {
  id: string;
  name: string;
  slug: string;
  fieldType: string;
  scope: string[];
}

interface ViewField {
  id: string;
  name: string;
  field: FieldDef;
}

interface ViewFieldEdge {
  node: ViewField;
}

interface ViewNode {
  id: string;
  viewfieldSet: {
    edges: ViewFieldEdge[];
  };
}

interface ViewEdge {
  node: ViewNode;
}

interface AllViewsData {
  edges: ViewEdge[];
}

interface FieldDefEdge {
  node: FieldDef;
}

interface AllFieldDefsData {
  edges: FieldDefEdge[];
}

interface ActiveField {
  id?: string;
  name: string;
  field: FieldDef;
}

interface DragItem {
  type: string;
  id?: string;
  index: number;
}

// Props interfaces
interface ShowMetadataTabProps {
  allAvailableFieldsData: AllFieldDefsData;
  MediaCardShowMetadataViewData: AllViewsData;
}

interface SelectedFieldsProps {
  activeFields: ActiveField[];
  setActiveFields: Dispatch<SetStateAction<ActiveField[]>>;
  onRemove: (field: ActiveField) => void;
}

interface SelectedFieldsItemsProps {
  onRemove: (field: ActiveField) => void;
  field: ActiveField;
  index: number;
  handleDrop2: (dragIndex: number, hoverIndex: number) => void;
}

interface FieldsListSearchProps {
  allAvailableFieldsData: FieldDef[];
  activeFields: ActiveField[];
  onSelect: (field: ActiveField) => void;
}

interface FieldsListSearchItemProps {
  a: FieldDef;
  index: number;
  onSelect: (field: ActiveField) => void;
  handleDrop1: (node: FieldDef) => void;
}

export default function ShowMetadataTabComponent(): React.ReactElement | null {
  const { data: allAvailableFields } = useQuery(allFieldDefinitionsQuery, {
    fetchPolicy: 'cache-and-network',
  });
  const { data: MediaCardShowMetadataViewData } = useQuery(allViews, {
    fetchPolicy: 'network-only',
    variables: {
      viewName: 'Library',
      componentName: 'MediaCardShowMetadata',
    },
  });

  if (!allAvailableFields) return null;
  if (!MediaCardShowMetadataViewData) return null;

  return (
    <ShowMetadataTab
      allAvailableFieldsData={allAvailableFields?.allFieldDefs}
      MediaCardShowMetadataViewData={MediaCardShowMetadataViewData?.allViews}
    />
  );
}

function ShowMetadataTab({
  allAvailableFieldsData,
  MediaCardShowMetadataViewData,
}: ShowMetadataTabProps): React.ReactElement {
  const classes = styles();
  const viewId = MediaCardShowMetadataViewData?.edges[0]?.node?.id;
  // these are ViewFields already selected when the component rendered,
  // each viewfield has a FieldDef structure in the all available fields, but they contain a nested field def
  // NOTE that availablefields to choose from are just FieldDefs, not ViewFields
  const originalActiveFields: ActiveField[] =
    MediaCardShowMetadataViewData?.edges[0]?.node?.viewfieldSet?.edges.map((a) => ({
      ...a.node,
    })) || [];

  const [currentActiveFields, setCurrentActiveField] = useState<ActiveField[]>(originalActiveFields);
  const [createFields] = useMutation(createViewFieldMutation);
  const [updateFields] = useMutation(updateViewFieldMutation);
  const [deleteFields] = useMutation(deleteViewFieldMutation);

  const allAvailableFields: FieldDef[] = allAvailableFieldsData?.edges
    .map((a) => a.node)
    .filter((a) => !currentActiveFields?.find((b) => b.field.id === a.id));

  // A comparer used to determine if two entries are equal.
  const duplicateObjectOfActiveAndDefault = (activeFields: ActiveField, defaultActiveFields: ActiveField): boolean =>
    activeFields.id === defaultActiveFields.id;
  // Get items that only occur in the left array,
  // using the compareFunction to determine equality.
  const onlyInLeft = (
    left: ActiveField[],
    right: ActiveField[],
    compareFunction: (a: ActiveField, b: ActiveField) => boolean,
  ): ActiveField[] => left.filter((leftValue) => !right.some((rightValue) => compareFunction(leftValue, rightValue)));
  const toBeDeletedFields = onlyInLeft(originalActiveFields, currentActiveFields, duplicateObjectOfActiveAndDefault);

  const createUpdateFieldsOnSubmit = (): void => {
    for (const [sequence, { field, id, name }] of currentActiveFields.entries()) {
      if (id) {
        updateFields({
          variables: {
            viewId,
            viewFieldId: id,
            fieldDefId: field.id,
            sequence,
            fieldLabel: name,
            fieldType: field.fieldType,
          },
        }).catch(() => {
          //toastr.error(e.message);
        });
        continue;
      }
      createFields({
        variables: {
          viewId,
          fieldDefId: field.id,
          fieldLabel: name,
          sequence,
          fieldType: field.fieldType,
          autoSequence: false,
        },
      });
    }
  };

  const formik = useFormik({
    initialValues: {},
    validationSchema: Yup.object({}),

    onSubmit: async () => {
      createUpdateFieldsOnSubmit();

      for (const { id } of toBeDeletedFields) {
        deleteFields({
          variables: {
            viewFieldId: id,
          },
        })
          .then(() => {
            // Field deleted successfully
          })
          .catch(() => {
            //toastr.error(e.message);
          });
      }
      toast.success('Show metadata fields updated successfully');
    },
  });

  const onClickReset = (): void => {
    setCurrentActiveField(originalActiveFields);
  };

  const addToActiveFields = (field: ActiveField): void => {
    setCurrentActiveField((v) => [...v, field]);
  };
  const removeFromActiveFields = (e: ActiveField): void =>
    setCurrentActiveField((v) => v.filter(({ field }) => field.id !== e.field.id));

  return (
    <Box
      sx={{
        width: '95%',
        margin: '0 auto',
        marginTop: '30px',
      }}
    >
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={GRID_CONTAINER_SPACING}>
          <Grid item xs={12}>
            <Title>Show Metadata Fields</Title>
          </Grid>
        </Grid>

        <Grid
          container
          spacing={GRID_CONTAINER_SPACING}
          style={{
            marginBottom: '30px',
          }}
        >
          <Grid item xs={4}>
            <div className={classes.cardTitle}>Available Fields</div>

            <FieldsListSearch
              allAvailableFieldsData={allAvailableFields}
              onSelect={addToActiveFields}
              activeFields={currentActiveFields}
            />
          </Grid>
          <Grid item xs={4}>
            <div className={classes.cardTitle}>Active Fields</div>

            <SelectedFields
              onRemove={removeFromActiveFields}
              activeFields={currentActiveFields}
              setActiveFields={setCurrentActiveField}
            />
          </Grid>
          <Grid item xs={4}>
            <div className={classes.enlargeTitleImage}>
              <div className={classes.enlargeImage}>
                <ImageHover
                  enlargeImageUrl={SHOW_METADATA_TAB_IMAGE}
                  altText="Show Metadata Tab Image"
                  defaultWidth={184}
                  defaultHeight={364}
                />
              </div>
            </div>
          </Grid>
        </Grid>

        <Grid container spacing={GRID_CONTAINER_SPACING}>
          <Grid className={classes.cancelSaveButtons} item xs={12}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={Object.keys(formik.errors).length > 0}
              style={{
                marginRight: '20px',
              }}
            >
              Save
            </Button>
            <Button onClick={onClickReset} color="secondary">
              Reset
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}

const SelectedFields: React.FC<SelectedFieldsProps> = ({ activeFields, setActiveFields, onRemove }) => {
  const classes = styles();
  const theme = useTheme();
  const handleDrop2 = (dragIndex2: number, hoverIndex2: number): void => {
    const draggedItem2 = activeFields[dragIndex2];
    setActiveFields((prevItems2) => {
      const updatedItems2 = [...prevItems2];
      updatedItems2.splice(dragIndex2, 1);
      updatedItems2.splice(hoverIndex2, 0, draggedItem2);
      return updatedItems2;
    });
  };

  const [{ isOver: isOver1 }, dropTarget1] = useDrop({
    accept: 'SHOW_METADATA_AVAILABLE_TO_ACTIVE',
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver(),
      handlerId1: monitor.getHandlerId(),
    }),
  });
  return (
    <div
      ref={(node) => {
        dropTarget1(node);
      }}
      className={classes.fieldContainer}
      style={{
        padding: '30px 15px',
        backgroundColor: isOver1 ? theme.palette.grey[400] : theme.palette.grey[500],
      }}
    >
      <div className={classes.fieldListContainer}>
        {activeFields.map((field, index) => (
          <SelectedFieldsItems
            key={field.id}
            onRemove={onRemove}
            field={field}
            index={index}
            handleDrop2={handleDrop2}
          />
        ))}
      </div>
    </div>
  );
};

const SelectedFieldsItems: React.FC<SelectedFieldsItemsProps> = ({ onRemove, field, index, handleDrop2 }) => {
  const classes = styles();
  const theme = useTheme();
  const id = field.id;

  const [{ isOver: isOver2 }, dropTarget2] = useDrop({
    accept: 'SHOW_METADATA_ACTIVE_TO_ACTIVE',
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver(),
      handlerId2: monitor.getHandlerId(),
    }),
    drop(item: DragItem) {
      const dragIndex2 = item.index;
      const hoverIndex2 = index;
      if (dragIndex2 === hoverIndex2) {
        return;
      }
      handleDrop2(dragIndex2, hoverIndex2);
      item.index = hoverIndex2;
    },
  });

  const [{ isDragging: isDragging2 }, dragTarget2] = useDrag({
    type: 'SHOW_METADATA_ACTIVE_TO_ACTIVE',
    item: { type: 'SHOW_METADATA_ACTIVE_TO_ACTIVE', id, index },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
    end: (_dragItem: DragItem, monitor: DragSourceMonitor) => {
      if (monitor.didDrop()) {
        // Drag completed
      }
    },
  });

  const onClickField = (node: ActiveField): void => {
    onRemove(node);
  };

  return (
    <div
      ref={(node) => {
        dropTarget2(node);
      }}
      className={classes.addedContentItem}
    >
      <div
        ref={(node) => {
          dragTarget2(node);
        }}
      >
        {field.name || field?.field?.name}
      </div>
      <button
        type="button"
        onClick={onClickField.bind(null, field)}
        style={{
          marginRight: '10px',
          display: 'flex',
          alignItems: 'center',
          opacity: isDragging2 ? 0.5 : 1,
          backgroundColor: isOver2 ? theme.palette.grey[400] : theme.palette.grey[500],
          cursor: 'pointer',
          border: 'none',
          padding: 0,
        }}
      >
        <CancelIcon className={classes.cancelIcon} fontSize={'medium'} />
      </button>
    </div>
  );
};

const FieldsListSearch: React.FC<FieldsListSearchProps> = ({ allAvailableFieldsData, activeFields, onSelect }) => {
  const classes = styles();
  const [searchVal, setSearchVal] = useState<string>('');

  const onChangeSearch = (e: ChangeEvent<HTMLInputElement>): void => {
    setSearchVal(e.target.value);
  };
  const handleDrop1 = (node: FieldDef): void => {
    onSelect({ field: node, name: node.name });
  };
  return (
    <div className={classes.fieldContainer}>
      <div className={classes.fieldSearchBox}>
        <InputBase
          className={classes.searchInput}
          placeholder={'Quick Search'}
          value={searchVal}
          onChange={onChangeSearch}
        />
      </div>
      <div className={classes.fieldListContainer}>
        {allAvailableFieldsData
          .filter((a) => !activeFields.find((b) => b.id === a.id))
          .filter((a) => a.name.toUpperCase().includes(searchVal.toUpperCase()))
          .map((a, index) => (
            <FieldsListSearchItem key={a.id} index={index} onSelect={onSelect} a={a} handleDrop1={handleDrop1} />
          ))}
      </div>
    </div>
  );
};

const FieldsListSearchItem: React.FC<FieldsListSearchItemProps> = ({ a, index, onSelect, handleDrop1 }) => {
  const classes = styles();
  const onClickField = (node: FieldDef): void => {
    onSelect({ field: node, name: node.name });
  };
  const id = a.id;
  const [{ isDragging: isDragging1 }, dragTarget1] = useDrag({
    type: 'SHOW_METADATA_AVAILABLE_TO_ACTIVE',
    item: { type: 'SHOW_METADATA_AVAILABLE_TO_ACTIVE', id, index },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
    end: (_dragItem: DragItem, monitor: DragSourceMonitor) => {
      if (monitor.didDrop()) {
        handleDrop1(a);
      }
    },
  });
  return (
    <button
      type="button"
      ref={(node) => {
        dragTarget1(node);
      }}
      style={{
        opacity: isDragging1 ? 0.5 : 1,
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
        width: '100%',
        textAlign: 'left',
      }}
      className={classes.fieldListItem}
      onClick={onClickField.bind(null, a)}
      key={index}
    >
      <div>{a.name}</div>
    </button>
  );
};
